import { createContext, useContext, useReducer } from 'react';
import { editorHandlers } from 'store/editorHandlers';

import type { Editor } from 'types';

// Reducer
type ActionType =
  | keyof typeof editorHandlers
  | 'connect'
  | 'disconnect'
  | 'setPreviewData'
  | 'updateContext'
  | 'error';

type Action = {
  error?: any;
  type: ActionType;
  update?: Partial<Editor>;
};

const reducer = (state: Editor, action: Action) => {
  if (action.type === 'connect') {
    return {
      ...state,
      editorHeaderStatus: '',
      editorIsDisabled: false,
      editorIsConnecting: false
    };
  } else if (action.type === 'disconnect') {
    return {
      ...state,
      editorHeaderStatus: 'connectionError',
      editorIsDisabled: true,
      editorIsConnecting: true
    };
  } else if (action.type === 'error') {
    return {
      ...state,
      editorConnectionError: action.error,
      editorIsDisabled: true,
      editorIsConnecting: false
    };
  } else if (action.type === 'updateContext') {
    return { ...state, ...action.update };
  }

  try {
    return {
      ...state,
      ...editorHandlers[action.type]({ pageState: state, data: action })
    };
  } catch (err) {
    return state;
  }
};

// Contexts and provider
const EditorDispatchContext = createContext(null);
const EditorStateContext = createContext(null);

interface ProviderProps extends React.PropsWithChildren {
  initialState: Partial<Editor>;
}

const EditorProvider = ({ children, initialState }: ProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <EditorStateContext.Provider value={state}>
      <EditorDispatchContext.Provider value={dispatch}>
        {children}
      </EditorDispatchContext.Provider>
    </EditorStateContext.Provider>
  );
};

// hooks
const useEditorContext = () => {
  const state = useContext<Editor>(EditorStateContext);
  return state;
};

const useEditorDispatch = () => {
  const dispatch = useContext<React.Dispatch<Action>>(EditorDispatchContext);
  return dispatch;
};

export { EditorProvider, useEditorContext, useEditorDispatch };
